import { createAsyncThunk } from '@reduxjs/toolkit';
import { ReportFetchCriteria, ReportService } from 'services/report.service';
import { SaveTempDrilldownCriteria } from 'core/models/dashboard';
import { ObjModel } from 'core/models';
import {
  IAutoDrilldownV2Data,
  IDataRefreshDetailsLoadRequestPayload, IFilterLoadRequestPayload, ILoadMoreDropdownOptionsPayload, IPaginationChangePayload, IReportConfigSaveRequestPayload, ISearchDropdownOptionsPayload, ISortingChangePayload,
} from 'core/models/report-redux';
import { AccessType, DimensionStatus, statusCodes } from 'core/constants/report';
import ReportBuilderService from 'services/report-builder.service';
import FilterFetchUtility from 'core/utils/filter-fetch-utility';
import { IGlobalState } from 'core/models/redux';
import {
  getDefaultEntity, getDrilldownReportDataLoad, getReportDataLoadPayload, getUpdatedReportConfig,
} from 'core/utils/report.util';
import {
  IColumn, IFilterConfig, IReportData, ISetDimensionsEntityGroupingPayload,
} from 'core/models/report-response';
import { isValidArrayAndNotEmpty, isValidObjectAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import cloneDeep from 'lodash/cloneDeep';
import { IVariables } from 'core/models/store';
import { isEmpty } from 'lodash';
import { FeatureContext, PageContext } from 'core/constants/common';
import SieraResponse from 'core/models/siera-response';
import { setSettingsError } from 'redux-v2/settings-store/settings-store.reducer';
import { MeasureIdentifier } from 'redux-v2/report-builder/report-builder-store.state';
import { getEntityJoinIdForMeasure } from 'components/feature/ReportBuilderV2/ReportBuilderDetail/DrilldownTabContent/drilldown.util';
import { filterBuilderToFilterConfig, getSavePayloadDimensionMetafields } from 'redux-v2/report-builder/report-builder-store.utils';
import { fetchMetaSelectorOptions } from 'core/utils/entity-grouping.util';
import { isListReport } from 'core/utils/report-builder.util';
import { expandWrapperFilters } from 'core/utils/dashboard.util';
import { getIdVariants, getWrapperRepresentativeFilterId } from 'core/utils/custom-filter-editor.util';
import { FilterType } from 'core/constants/filter';
import { IFetchDrilldownPayload, ReportActionTypes } from './report-store.state';
import { getStateOnDrilldownSortColumn, getStateOnPaginationChange } from './report-store.utils';
import {
  drilldownSortingChange, getTempAutodrilldownStateId, loadAutodrilldown, reportAutodrilldown,
  autodrilldownLoadFailure, autodrilldownLoadSuccess,
  reportLoadMoreOptions, reportSearchDropdownOptions, reportPaginationChange,
  reportSetEntityGroupingOptions,
} from './report-store.reducer';

export const getReportConfig = createAsyncThunk(ReportActionTypes.REPORT_CONFIG_LOAD_REQUEST,
  async (params: { reportId: string, sourceId: string, variables: IVariables, drilldownStateId?: string }) => {
    let drilldownStatePayload: ObjModel.Obj | null = null;
    if (params.drilldownStateId) {
      drilldownStatePayload = await new ReportService().getDrilldownState(params.drilldownStateId) as ObjModel.Obj;
    }
    const response = await new ReportService().getReportConfig(
      { reportId: params.reportId, sourceId: params.sourceId },
      drilldownStatePayload,
    );
    // Surface the temp drilldown state on the action so the reducer can propagate
    // custom-filter values onto the target report's appliedFilters. Render config
    // flattens only constituent filters, so a temp-state value keyed by a target
    // custom-filter ID would otherwise be dropped during filterConfig hydration.
    return { ...response, drilldownStatePayload };
  });

export const getFiltersLoad = createAsyncThunk(ReportActionTypes.FILTER_LOAD_REQUEST,
  async (params: IFilterLoadRequestPayload, { getState }) => {
    const { auth, reportStoreV2, reportBuilder } = getState() as IGlobalState;
    const { variables, userAttributes } = auth;
    const { filterId, filterValue, isLazyLoad } = params;
    const {
      filterConfig, appliedFilters, isResetClicked, reportConfig, drilldownPoint, savedReportConfig,
    } = reportStoreV2;

    const {
      assortedInfo: { reportId },
    } = reportBuilder;

    const {
      reportId: alternateReportId,
    } = reportStoreV2;

    let featureContextInfo = reportConfig?.FeatureContext;
    if (Object.prototype.hasOwnProperty.call(reportConfig, 'FeatureContext') && !reportConfig?.FeatureContext) {
      featureContextInfo = FeatureContext.Sales;
    }
    const userContextInfo = reportConfig?.UserContext;
    const flatHierarchy = !reportConfig.AllowUserDrillDown;
    const filter = filterConfig.find((item) => item.ID === filterId);
    // Resolve parent filter value. If LinkedTo points to a constituent that is the
    // REPRESENTATIVE constituent (FilterKey, else first linked) of a custom filter, read
    // the value from that wrapper's appliedFilters — the wrapper stands in for that parent.
    // If the parent is merely a non-key constituent, the wrapper no longer represents it
    // (e.g. the FilterKey was moved to another constituent), so the dependency is broken
    // and we must NOT inherit the wrapper's value.
    // Augment the lookup pool with sub-query (SubBuilder) filters so a dependent filter whose
    // parent lives in a sub-query can be matched. The main filterConfig only carries sub-query
    // filters flagged ShowInMainReport; the rest live in savedReportConfig.SubBuilders. Passing
    // isSubBuilder=false to filterBuilderToFilterConfig includes ALL sub-query filters (skips the
    // ShowInMainReport gating) and recurses SubFilters. Scoped to parent-value resolution only.
    const subQueryFilters = (savedReportConfig?.SubBuilders || []).flatMap(
      (builder) => filterBuilderToFilterConfig(builder.Filter, [], [], false),
    );
    const parentLookupFilterConfig = isValidArrayAndNotEmpty(subQueryFilters)
      ? [...filterConfig, ...subQueryFilters]
      : filterConfig;
    let value = null;
    const isFilterToReloadACustomFilter = filter.IsCustomFilter;
    const filterKeyOfCustomFilterToReload = isFilterToReloadACustomFilter ? filter?.Metadata?.FilterKey : '';
    const correspondingFilterForFilterKey = isFilterToReloadACustomFilter ? parentLookupFilterConfig?.find((f) => !f.IsCustomFilter && f.Metadata?.FilterKey === filterKeyOfCustomFilterToReload) : ({} as IFilterConfig);

    if (filter.LinkedTo || correspondingFilterForFilterKey?.LinkedTo || correspondingFilterForFilterKey?.Render?.LinkedTo) {
      const linkedFilter = parentLookupFilterConfig.find((item) => item.ID === filter.LinkedTo || item.ID === correspondingFilterForFilterKey?.LinkedTo || item.ID === correspondingFilterForFilterKey?.Render?.LinkedTo);
      const parentCfId = linkedFilter?.AssociatedCustomFilterId;
      const parentDef = parentCfId
        ? reportConfig?.CustomFilterDefinitions?.find((d) => d.ID === parentCfId)
        : undefined;
      const representativeId = getWrapperRepresentativeFilterId(parentDef);
      const isRepresentative = !!representativeId && !!linkedFilter
        && getIdVariants(linkedFilter.ID, linkedFilter.QueryId).includes(representativeId);
      const resolvedId = isRepresentative ? parentCfId : filter.LinkedTo;
      // Only break the dependency when the parent IS a custom-filter constituent that is no
      // longer the wrapper's representative (e.g. the FilterKey was moved to another
      // constituent). A plain parent dropdown that was never part of a custom filter has no
      // parentDef and must still cascade its value — otherwise ParentDropDownValues ships empty.
      const isCustomFilterConstituent = !!parentDef;
      if (isCustomFilterConstituent && !isRepresentative && filter.Type !== FilterType.UserMultiSelect) {
        value = null;
      } else {
        value = isValidObjectAndNotEmpty(appliedFilters[resolvedId]) ? appliedFilters[resolvedId].FilterResponse : null;
      }
    }
    if (filter.CustomHierarchyId) {
      value = appliedFilters[filter.ID].FilterResponse;
    }
    if (filter.Type === FilterType.UserMultiSelect && value === null && !filter.IsPrimaryUserFilter) {
      value = variables.SER_USER_AUTO_ID; // for getting the autoUserId from auth for sales user flow (as the pre-applied filter is only linked to primary user filter)
    }
    const filterPayload = new FilterFetchUtility({ ...variables, reportId: reportId || alternateReportId }).getUserAdditionalFilterPayload(
      appliedFilters,
      filterConfig,
      isResetClicked,
    );
    return new FilterFetchUtility({ ...variables, reportId: reportId || alternateReportId }).GetSingleFilterData(filter,
      [
        FilterType.LSQMetadataMultiSelect,
        FilterType.LSQMetadataSingleSelect,
        FilterType.CustomDefinedMultiSelect,
        FilterType.CustomDefinedSingleSelect,
      ].includes(filterConfig.find((item) => item.ID === filterId && item.CustomHierarchyId)?.Type) ? null : value,
      isLazyLoad
        ? filterValue
        : appliedFilters[filterId].FilterResponse,
      appliedFilters[filterId],
      filterPayload, featureContextInfo, flatHierarchy, !!drilldownPoint && drilldownPoint.level !== userAttributes?.Name, userContextInfo);
  });

export const getReportData = createAsyncThunk(ReportActionTypes.REPORT_DATA_LOAD_REQUEST,
  async (params: string, { getState, rejectWithValue }) => {
    const { reportBuilder, reportStoreV2, auth } = getState() as IGlobalState;
    const { userAttributes } = auth;
    const {
      filterConfig, reportConfig, activeFilters, sort, page, columns, activeDimensions, appliedDimensions,
      allMeasures, dynamicDimensions, reportId, savedReportConfig, entityGroupStore, entityGroupOptionsMap,
      customHierarchyDrilldownData,
      drilldownPoint, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload,
    } = reportStoreV2;
    const dimensionMetaFields = savedReportConfig?.DimensionsMetaFields || [];
    const isReportListType = isListReport(savedReportConfig?.Builder[0]?.Type);
    const data = getReportDataLoadPayload(filterConfig, activeFilters, reportId, sort, page, columns, activeDimensions, allMeasures, dynamicDimensions, entityGroupStore, entityGroupOptionsMap, isReportListType, reportConfig.FiltersMetadata, reportConfig.CustomHierarchies, customHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload, savedReportConfig?.SubBuilders, savedReportConfig?.CustomFilterDefinitions);
    data.DimensionsMetaFields = getSavePayloadDimensionMetafields(reportBuilder, dimensionMetaFields, appliedDimensions, false, entityGroupStore, entityGroupOptionsMap);
    data.FeatureContext = reportConfig.FeatureContext || FeatureContext.Sales;
    data.UserDrilldownActive = !!drilldownPoint && drilldownPoint.level !== userAttributes?.Name && isEmpty(customHierarchyDrilldownData);
    try {
      return await new ReportService().getReportData(data);
    } catch (e: any) {
      const errorData = e?.data || e?.response?.data;
      const ErrorMessage = errorData?.ErrorMessage || errorData?.Message || e?.message;
      return rejectWithValue({ error: { ErrorMessage } });
    }
  });
export const getRawConfig = createAsyncThunk(ReportActionTypes.GET_RAW_CONFIG_LOAD_REQUEST,
  // sourceId (<dashId:panelId>) is passed on the open-in-new-tab path so the BE returns the
  // baked temp-view config — keeps wrapper-linked dashboard filters (incl. all-SubBuilder
  // wrappers) on the raw-config load. Omitted/undefined for normal loads.
  async (params: { reportId: string, sourceId?: string }) => new ReportService().getRawConfig(params));

export const getDynamicDimensions = createAsyncThunk(ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST,
  async (params:{measureFieldInfo?: MeasureIdentifier }, { getState }) => {
    const { reportStoreV2 } = getState() as IGlobalState;
    const { measureFieldInfo } = params;
    if (measureFieldInfo) {
      return new ReportBuilderService().getAdditionalDrilldownColumns(reportStoreV2.reportId, measureFieldInfo);
    }
    return new ReportService().getDynamicDimensions(reportStoreV2.reportId);
  });
export const getDynamicFilters = createAsyncThunk(ReportActionTypes.DYNAMIC_FILTERS_LOAD_REQUEST,
  async (reportId:string) => new ReportService().getDynamicFilters(reportId));
export const getDynamicMeasures = createAsyncThunk(ReportActionTypes.REPORT_DYNAMIC_MEASURES_LOAD_REQUEST,
  async (reportId: string) => new ReportService().getDynamicMeasures(reportId));
export const getFetchDrilldownReport = createAsyncThunk(ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST,
  async (params: IFetchDrilldownPayload, { getState, dispatch }) => {
    const {
      page,
      sort,
      selectedMeasureInfo,
    } = params;
    dispatch(loadAutodrilldown());
    const { reportStoreV2, auth } = getState() as IGlobalState;
    const { userAttributes } = auth;
    const newState = cloneDeep(reportStoreV2);

    newState.autodrilldownData = { ...newState.autodrilldownData, sort: sort || newState.autodrilldownData.sort, page: page || newState.autodrilldownData.page };
    const {
      filterConfig, reportConfig, savedReportConfig, activeFilters, reportId, columns, activeDimensions, allMeasures, autodrilldownData, dynamicDimensions, entityGroupStore, entityGroupOptionsMap, customHierarchyDrilldownData, drilldownStatePayload,
    } = newState;

    // Use customHierarchyDrilldownData.columns if we're in a custom hierarchy drilldown state
    // Otherwise use the regular columns
    const columnsToUse = isValidArrayAndNotEmpty(customHierarchyDrilldownData?.columns) ? customHierarchyDrilldownData?.columns : columns;

    const fetchDrillDownRequest = getDrilldownReportDataLoad(reportId, autodrilldownData, filterConfig, columnsToUse, activeDimensions, allMeasures, activeFilters, dynamicDimensions, entityGroupStore, entityGroupOptionsMap, customHierarchyDrilldownData, reportConfig.FiltersMetadata, reportConfig.CustomHierarchies, undefined, drilldownStatePayload, savedReportConfig?.SubBuilders, savedReportConfig?.CustomFilterDefinitions);
    fetchDrillDownRequest.FeatureContext = reportConfig?.FeatureContext || FeatureContext.Sales;
    fetchDrillDownRequest.UserDrilldownActive = !!reportStoreV2?.drilldownPoint && reportStoreV2.drilldownPoint.level !== userAttributes?.Name && isEmpty(customHierarchyDrilldownData);

    return new ReportService().getFetchDrilldownReport(fetchDrillDownRequest, { isRefresh: params?.isRefresh })
      .then((res: IReportData) => {
        if (isEmpty(reportStoreV2?.autodrilldownData?.drilldownFields)) {
          dispatch(getDynamicDimensions({ measureFieldInfo: selectedMeasureInfo }));
        }
        dispatch(autodrilldownLoadSuccess(res));
      })
      .catch((e) => {
        console.error(e);
        const errorData = e?.data || e?.response?.data;
        dispatch(autodrilldownLoadFailure(errorData));
      });
  });
export const getTempDrilldownState = createAsyncThunk(ReportActionTypes.AUTODRILLDOWN_V2,
  async (stateId: string, { getState, dispatch }) => {
    const { reportStoreV2 } = getState() as IGlobalState;
    new ReportService().getDrilldownState(stateId).then((res: IAutoDrilldownV2Data) => {
      dispatch(reportAutodrilldown({
        selectedMeasure: res.SelectedMeasure,
        selectedRowData: res.SelectedRow,
        measureFieldAlias: res.MeasureFieldAlias,
      }));
      if (reportStoreV2?.reportConfig?.Settings?.IsNormalised) {
        dispatch(getDataRefreshStatus({ reportId: reportStoreV2.reportId }));
      } else {
        dispatch(getDataRefreshDetails({ reportId: reportStoreV2.reportId }));
      }

      dispatch(getTempAutodrilldownStateId(res));
      dispatch(getFetchDrilldownReportV2(res));
    }).catch();
  });
export const getFetchDrilldownReportV2 = createAsyncThunk(ReportActionTypes.AUTODRILLDOWN_REPORTV2_DATA_LOAD_REQUEST,
  async (params: IAutoDrilldownV2Data, { getState, dispatch }) => {
    const { reportStoreV2 } = getState() as IGlobalState;
    const {
      autodrilldownData, reportConfig, reportId, columns, filterConfig,
    } = reportStoreV2;
    const drilldownFields : IColumn[] = isValidArrayAndNotEmpty(autodrilldownData.drilldownFields) ? autodrilldownData.drilldownFields.filter(
      (dim) => dim.Props && dim.Props.Dimension && dim.Props.Dimension.Applied === DimensionStatus.Applied,
    ) : null;
    // Dashboard ships custom-filter values keyed by the wrapper ID. The BE bake on the
    // drilldown fetch (overwriteGlobalFilters) only matches constituent payload keys, so
    // fan the wrapper entries out to their constituents here. The dashboard value is
    // already the raw per-constituent value, and the User select-type rides in
    // params.FiltersMetadata (getLinkedUserFilterType) which flows through via ...params.
    const expandedFilters = expandWrapperFilters(params.Filters, filterConfig);
    const data = {
      ...params,
      Filters: expandedFilters,
      DrilldownColumns: drilldownFields,
      Page: autodrilldownData.page,
      Sort: autodrilldownData.sort,
      FeatureContext: reportConfig?.FeatureContext || FeatureContext.Sales,
    };

    return new ReportService().getFetchDrilldownReportV2(data).then((res) => {
      Promise.all([
        new ReportService().getRawConfig({ reportId }),
        new ReportBuilderService().getFieldsRequest(reportId),
      ]).then(([rawReportConfig, fields]) => {
        const selectedColumn = isValidArrayAndNotEmpty(columns) && columns.find((col) => col.Id === autodrilldownData?.selectedMeasure);
        const defaultEntity = getDefaultEntity(rawReportConfig.data);
        const measureInfo = getEntityJoinIdForMeasure(fields, selectedColumn, defaultEntity);
        if (isEmpty(reportStoreV2.autodrilldownData.drilldownFields)) {
          dispatch(getDynamicDimensions({ measureFieldInfo: measureInfo }));
        }
      }).catch((error) => {
        console.error(error);
        const errorData = error?.data || error?.response?.data;
        dispatch(autodrilldownLoadFailure(errorData));
      });
      dispatch(autodrilldownLoadSuccess(res));
    }).catch();
  });
export const getExportLists = createAsyncThunk(ReportActionTypes.EXPORT_LIST_LOAD_REQUEST,
  async () => new ReportService().getExportListData());
export const getExportUsageStatus = createAsyncThunk(ReportActionTypes.EXPORT_USAGE_STATUS_REQUEST,
  async () => new ReportService().getExportUsageStatus());
export const getExportListDataByIds = createAsyncThunk(ReportActionTypes.EXPORT_LIST_LOAD_BY_IDS_REQUEST,
  async (exportIds: string[]) => new ReportService().getExportListDataByIds(exportIds));
export const getRetryExportDatagetRetryExportData = createAsyncThunk(ReportActionTypes.RETRY_EXPORT_REQUEST,
  async (exportId: string) => new ReportService().getRetryExportData({ ExportId: exportId }));

export const getDataRefreshDetails = createAsyncThunk(ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST,
  async (params: IDataRefreshDetailsLoadRequestPayload) => new ReportService().getDataRefreshDetails({ reportId: params.reportId }));

export const getDataRefreshStatus = createAsyncThunk(ReportActionTypes.DATA_REFRESH_STATUS_LOAD_REQUEST,
  async (params: IDataRefreshDetailsLoadRequestPayload) => new ReportService().getDataRefreshStatus({ reportId: params.reportId }));

export const accesscontrol = createAsyncThunk(ReportActionTypes.ACCESS_CONTROL_REQUEST,
  async (params: {reportId: string, type: AccessType, isPolling?: boolean}) => new ReportService().accesscontrol(params.reportId, params.type));
export const deleteReport = createAsyncThunk(ReportActionTypes.DELETE_REPORT,
  async (reportId: string) => new ReportService().deleteReport(reportId));
export const saveReportConfig = createAsyncThunk<Object, IReportConfigSaveRequestPayload, { rejectValue: SieraResponse<string>}
  >(ReportActionTypes.SAVE_REPORT_CONFIG_REQUEST,
    async (params: IReportConfigSaveRequestPayload, { getState, rejectWithValue, dispatch }) => {
      try {
        const { reportStoreV2, reportBuilder } = getState() as IGlobalState;
        const {
          appliedDimensions, savedReportConfig, entityGroupStore, entityGroupOptionsMap,
        } = reportStoreV2;
        const { isSaveAs } = params as IReportConfigSaveRequestPayload;
        const updatedReportConfig = getUpdatedReportConfig(cloneDeep(reportStoreV2), isSaveAs);
        const dimensionMetaFields = savedReportConfig?.DimensionsMetaFields || [];
        updatedReportConfig.DimensionsMetaFields = getSavePayloadDimensionMetafields(reportBuilder, dimensionMetaFields, appliedDimensions, false, entityGroupStore, entityGroupOptionsMap);
        const result = await new ReportBuilderService().saveReportConfig(updatedReportConfig, { PageContext: isSaveAs ? PageContext.SAVE_AS : PageContext.EDIT });
        return result;
      } catch (err) {
        if ((err as Response)?.status === statusCodes.Unprocessable) dispatch(setSettingsError(true));
        return rejectWithValue({
          status: (err as Response)?.status || 500,
          data: (err as Error).message || 'Something went wrong',
        });
      }
    });
export const getExportData = createAsyncThunk(ReportActionTypes.EXPORT_DATA_LOAD_REQUEST, async (fileType: string, { getState }) => {
  const { reportBuilder, reportStoreV2 } = getState() as IGlobalState;
  const {
    filterConfig, activeFilters, reportId, sort, page, columns, activeDimensions, appliedDimensions,
    allMeasures, autodrilldownData, dynamicDimensions, reportConfig, savedReportConfig, entityGroupStore, entityGroupOptionsMap,
    customHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload,
  } = reportStoreV2;
  let data:ReportFetchCriteria;
  if (!autodrilldownData?.reportData) {
    const dimensionMetaFields = savedReportConfig?.DimensionsMetaFields || [];
    const isReportListType = isListReport(savedReportConfig?.Builder[0]?.Type);
    data = getReportDataLoadPayload(filterConfig, activeFilters, reportId, sort, page, columns, activeDimensions, allMeasures, dynamicDimensions, entityGroupStore, entityGroupOptionsMap, isReportListType, reportConfig.FiltersMetadata, reportConfig.CustomHierarchies, customHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload, savedReportConfig?.SubBuilders, savedReportConfig?.CustomFilterDefinitions);
    data.DimensionsMetaFields = getSavePayloadDimensionMetafields(reportBuilder, dimensionMetaFields, appliedDimensions, false, entityGroupStore, entityGroupOptionsMap);
  } else {
    data = getDrilldownReportDataLoad(reportId, autodrilldownData, filterConfig, columns, activeDimensions, allMeasures, activeFilters, dynamicDimensions, entityGroupStore, entityGroupOptionsMap, customHierarchyDrilldownData, reportConfig.FiltersMetadata, reportConfig.CustomHierarchies, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload, savedReportConfig?.SubBuilders, savedReportConfig?.CustomFilterDefinitions);
  }

  return new ReportService().getExportData(data, fileType);
});

export const getExportDataV2 = createAsyncThunk(ReportActionTypes.DASHBOARD_REDIRECTED_EXPORT_DATA_LOAD_REQUEST, async (fileType: string, { getState }) => {
  const { reportStoreV2 } = getState() as IGlobalState;
  const {
    autoDrilldownDataV2, autodrilldownData,
  } = reportStoreV2;
  const drilldownFields : IColumn[] = autodrilldownData.drilldownFields.filter(
    (dim) => dim.Props && dim.Props.Dimension && dim.Props.Dimension.Applied === DimensionStatus.Applied,
  );
  const data = {
    ...autoDrilldownDataV2,
    DrilldownColumns: drilldownFields,
    Page: autodrilldownData.page,
  };

  return new ReportService().getExportDataV2(data, fileType);
});

export const loadFilterMoreOptions = createAsyncThunk(ReportActionTypes.LOAD_MORE_DROPDOWN_OPTIONS, async (params: ILoadMoreDropdownOptionsPayload, { getState, dispatch }) => {
  dispatch(reportLoadMoreOptions(params));
  const { auth: { variables } } = getState() as IGlobalState;
  dispatch(getFiltersLoad({
    filterId: params.filterId, filterValue: params.filterValue, variables, isLazyLoad: true,
  }));
});

export const searchFilterOptions = createAsyncThunk(ReportActionTypes.SEARCH_DROPDOWN_OPTIONS, async (params: ISearchDropdownOptionsPayload, { getState, dispatch }) => {
  dispatch(reportSearchDropdownOptions(params));
  const { auth: { variables } } = getState() as IGlobalState;
  dispatch(getFiltersLoad({
    filterId: params.filterId, filterValue: params.filterValue, variables, isLazyLoad: true,
  }));
});

export const autoDrilldownPaginationChange = createAsyncThunk(ReportActionTypes.PAGINATION_CHANGE, async (params: IPaginationChangePayload, { getState, dispatch }) => {
  const { reportStoreV2 } = getState() as IGlobalState;
  dispatch(reportPaginationChange(params));
  const {
    paginationKey, paginationValue, isAutodrilldown,
  } = params;
  const newState = getStateOnPaginationChange(
    cloneDeep(reportStoreV2),
    paginationKey,
    paginationValue,
    isAutodrilldown,
  );
  const { autodrilldownData, autoDrilldownDataV2 } = newState;
  if (autoDrilldownDataV2?.DataSourceId) {
    dispatch(getFetchDrilldownReportV2({ ...autoDrilldownDataV2, Page: autodrilldownData?.page }));
  } else {
    dispatch(getFetchDrilldownReport({ ...autoDrilldownDataV2, page: autodrilldownData?.page }));
  }
});

export const autoDrilldownSortingChange = createAsyncThunk(ReportActionTypes.DRILLDOWN_SORTING_CHANGE, async (params: ISortingChangePayload, { getState, dispatch }) => {
  const { reportStoreV2 } = getState() as IGlobalState;
  dispatch(drilldownSortingChange(params));
  const {
    columnType,
    columnName,
    order,
    multiSort,
    Id,
  } = params;
  const newState = getStateOnDrilldownSortColumn(
    cloneDeep(reportStoreV2),
    columnType,
    columnName,
    order,
    multiSort,
    Id,
  );
  const { autoDrilldownDataV2, autodrilldownData } = newState;
  if (autoDrilldownDataV2?.DataSourceId) {
    dispatch(getFetchDrilldownReportV2({ ...autoDrilldownDataV2, Sort: autodrilldownData?.sort }));
  } else {
    dispatch(getFetchDrilldownReport({ ...autodrilldownData, sort: autodrilldownData?.sort }));
  }
});

export const getExpressionFields = createAsyncThunk(ReportActionTypes.GET_EXPRESSION_FIELDS,
  async (reportId:string) => new ReportBuilderService().getFieldsRequest(reportId));

export const getMetaSelectorOptions = createAsyncThunk(ReportActionTypes.GET_META_SELECTOR_OPTIONS,
  async (params: ISetDimensionsEntityGroupingPayload, { dispatch }) => {
    const response = await fetchMetaSelectorOptions(params);
    dispatch(reportSetEntityGroupingOptions({
      res: response,
      data: params.data,
      isCreate: params.mode !== undefined,
      activeId: params.activeId,
    }));
    return response;
  });

export const saveLinkedInsightDrilldown = createAsyncThunk(
  ReportActionTypes.SAVE_LINKED_INSIGHT_DRILLDOWN,
  async (params: { payload: SaveTempDrilldownCriteria; targetReportId: string }) => {
    const response = await new ReportBuilderService().saveTempDrilldownState(params.payload);
    return { stateId: response.TempReportId, targetReportId: params.targetReportId };
  },
);
